import { useEffect, useRef, useState } from 'react';
import { Modal, Stack, Text, Button, Group, Center, Loader, Alert, Box } from '@mantine/core';
import QRCode from 'qrcode';
import { RefreshCw, Check } from 'lucide-react';
import { devicesApi, type PairingSession } from '../api';
import { numStyle } from '../theme';
import { formatCountdown } from '../utils/format';

interface Props {
  opened: boolean;
  onClose: () => void;
  onPaired: () => void;
}

export function PairingModal({ opened, onClose, onPaired }: Props) {
  const [session, setSession] = useState<PairingSession | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const pairedRef = useRef(false);

  const start = async () => {
    setLoading(true);
    setError(null);
    setQr(null);
    pairedRef.current = false;
    try {
      const s = await devicesApi.initiatePairing();
      setSession(s);
      setQr(await QRCode.toDataURL(s.qrCodeData, { width: 320, margin: 1 }));
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Kód se nepodařilo vytvořit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (opened) start();
    else { setSession(null); setQr(null); setError(null); }
  }, [opened]);

  // Odpočet platnosti kódu (backend ho drží 10 minut).
  useEffect(() => {
    if (!session) return;
    const expires = new Date(session.expiresAt).getTime();
    const tick = () => setRemaining(Math.max(0, Math.floor((expires - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [session]);

  // Dokud kód platí, ptáme se serveru, jestli už se mobil spároval.
  useEffect(() => {
    if (!session || !opened) return;
    const id = window.setInterval(async () => {
      try {
        const done = await devicesApi.checkPairingStatus(session.pairingCode);
        if (done && !pairedRef.current) {
          pairedRef.current = true;
          window.clearInterval(id);
          onPaired();
        }
      } catch {
        /* průběžné selhání dotazu nechceme hlásit — kód pořád platí */
      }
    }, 3000);
    return () => window.clearInterval(id);
  }, [session, opened, onPaired]);

  const expired = remaining <= 0 && !!session;

  return (
    <Modal opened={opened} onClose={onClose} title="Spárovat zařízení">
      <Stack gap="sm">
        <Text c="dimmed" fz="sm">
          V mobilní aplikaci vyber <b>Spárovat</b> a naskenuj kód.
        </Text>

        {loading && <Center h={200}><Loader /></Center>}
        {error && <Alert color="red" title="Párování se nepovedlo">{error}</Alert>}

        {qr && !expired && (
          <>
            <Center>
              <Box p="sm" bg="white" style={{ borderRadius: 6, border: '1px solid var(--mantine-color-default-border)' }}>
                <img src={qr} alt="Párovací QR kód" width={180} height={180} style={{ display: 'block' }} />
              </Box>
            </Center>
            <Text ta="center" fw={700} fz={21} style={{ ...numStyle, letterSpacing: '0.2em' }} c="forest">
              {session?.pairingCode}
            </Text>
            <Text ta="center" c="dimmed" fz="sm">
              Platí ještě {formatCountdown(remaining * 1000)}
            </Text>
          </>
        )}

        {expired && (
          <Alert color="orange" title="Kód vypršel">
            Platnost byla 10 minut. Vygeneruj nový a naskenuj ho znovu.
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Zavřít</Button>
          <Button leftSection={expired ? <RefreshCw size={14} /> : <Check size={14} />} onClick={start} loading={loading}>
            Nový kód
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
